// js/views/crm-objects.ts — Twenty custom obje (intake/delivery/supportTicket)
// READ-ONLY kokpit (W4-objects). Endpoint: GET /api/v2/crm/objects (ref tablo aynası).
// SADECE OKUMA. Twenty'ye yazma YOK · müşteriye gönderim YOK · approval bypass YOK.
// Mevcut dashboard sınıfları (view-header, gb-card, gb-kpi-grid, gb-kpi-label,
// gb-pill, muted); yeni CSS/asset YOK. Durum rengi yerine ✓/⏳/⚠ ikon (a11y).
// crm-poc.ts'e DOKUNULMADI — bu ayrı bir view (dashboard #1/#7/#8).

import { escHtml } from '../utils.js';
import { apiGetJson } from '../api.js';

interface IntakeRow {
  castagent_ref: string; twenty_id: string | null; intake_source: string | null;
  raw_summary: string | null; received_at: string | null; opportunity_ref: string | null;
  status: string | null; synced_at: string | null;
}
interface DeliveryRow {
  castagent_ref: string; twenty_id: string | null; deliverable: string | null;
  delivery_status: string | null; accepted_at: string | null; opportunity_ref: string | null;
  synced_at: string | null;
}
interface SupportRow {
  castagent_ref: string; twenty_id: string | null; severity: string | null; sla: string | null;
  owner_ref: string | null; status: string | null; company_ref: string | null; synced_at: string | null;
}
interface ObjectsResponse {
  ok: true;
  counts: { intakes: number; deliveries: number; supportTickets: number };
  intakes: IntakeRow[];
  deliveries: DeliveryRow[];
  supportTickets: SupportRow[];
}

type ViewState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; data: ObjectsResponse }
  | { kind: 'error'; message: string };

let viewState: ViewState = { kind: 'idle' };

function str(v: unknown, fallback = '—'): string {
  const s = typeof v === 'string' ? v : v == null ? '' : String(v);
  return s.length ? s : fallback;
}

export async function loadCrmObjects(): Promise<void> {
  viewState = { kind: 'loading' };
  const r = await apiGetJson<ObjectsResponse>('/api/v2/crm/objects');
  if (r.ok) viewState = { kind: 'ready', data: r.data };
  else viewState = { kind: 'error', message: r.message ?? r.error };
}

export function renderCrmObjects(): void {
  const root = document.getElementById('view-crm-objects');
  if (!root) return;
  root.innerHTML = renderCrmObjectsHtml(viewState);
}

function kpi(label: string, value: string): string {
  return `<div><div class="gb-kpi-label">${escHtml(label)}</div><div class="gb-kpi-value">${escHtml(value)}</div></div>`;
}

/** Bir custom-obje tablosunu kart olarak render eder (boşsa açıklayıcı not). */
function objectCard(title: string, sub: string, count: number, rowsHtml: string, emptyNote: string): string {
  const body = count > 0
    ? `<ul style="list-style:none;padding:0;margin:8px 0 0">${rowsHtml}</ul>`
    : `<div class="gb-empty-sm">${escHtml(emptyNote)}</div>`;
  return `<div class="gb-card" style="margin-top:12px">
    <div class="gb-kpi-label">${escHtml(title)} (${count})</div>
    <div class="muted" style="margin-bottom:4px">${escHtml(sub)}</div>
    ${body}
  </div>`;
}

/** status='new' = otomatik yakalanan, triage bekleyen ham sinyal (a11y: ikon + metin). */
function isPendingIntake(o: IntakeRow): boolean {
  return (o.status ?? '').toLowerCase() === 'new';
}

function intakeRow(o: IntakeRow): string {
  const oppBadge = o.opportunity_ref ? `<span class="gb-pill">→ ${escHtml(o.opportunity_ref)}</span>` : '';
  // Durum rozeti — renk-tek-başına değil (a11y): new → ⏳ bekliyor, diğer → metin.
  const st = (o.status ?? '').toLowerCase();
  const statusBadge = st
    ? `<span class="gb-pill">${st === 'new' ? '⏳ ' : ''}${escHtml(str(o.status))}</span>`
    : '';
  return `<li style="margin:6px 0">
    <b>📥 ${escHtml(str(o.intake_source))}</b>
    <span class="muted"> — ${escHtml(str(o.raw_summary))}</span>
    ${statusBadge}
    ${oppBadge}
    <span class="muted"> · alındı: ${escHtml(str(o.received_at))} · senkron: ${escHtml(str(o.synced_at))}</span>
  </li>`;
}

function deliveryRow(o: DeliveryRow): string {
  // Durum ikonu (renk-tek-başına değil — a11y): accepted/closed → ✓, delivered/review → ⏳, diğer → •.
  const st = (o.delivery_status ?? '').toLowerCase();
  const icon = st.includes('accept') || st.includes('closed') ? '✓'
    : st.includes('deliver') || st.includes('review') ? '⏳' : '•';
  const oppBadge = o.opportunity_ref ? `<span class="gb-pill">→ ${escHtml(o.opportunity_ref)}</span>` : '';
  return `<li style="margin:6px 0">
    <b>${icon} ${escHtml(str(o.deliverable))}</b>
    <span class="muted"> — ${escHtml(str(o.delivery_status))}</span>
    ${oppBadge}
    <span class="muted"> · kabul: ${escHtml(str(o.accepted_at))} · senkron: ${escHtml(str(o.synced_at))}</span>
  </li>`;
}

function supportRow(o: SupportRow): string {
  // Önem ikonu (renk-tek-başına değil): critical/high → ⚠, diğer → •.
  const sev = (o.severity ?? '').toLowerCase();
  const icon = sev.includes('critical') || sev.includes('high') ? '⚠' : '•';
  const compBadge = o.company_ref ? `<span class="gb-pill">→ ${escHtml(o.company_ref)}</span>` : '';
  return `<li style="margin:6px 0">
    <b>${icon} ${escHtml(str(o.severity))}</b>
    <span class="muted"> — ${escHtml(str(o.status))} · SLA: ${escHtml(str(o.sla))} · sahip: ${escHtml(str(o.owner_ref))}</span>
    ${compBadge}
    <span class="muted"> · senkron: ${escHtml(str(o.synced_at))}</span>
  </li>`;
}

export function renderCrmObjectsHtml(s: ViewState): string {
  const header = `
    <div class="view-header">
      <div>
        <div class="view-title">CRM Objeleri</div>
        <div class="view-sub">Twenty custom objeler — Intake · Delivery · Support Ticket (read-only ayna). Yazma/gönderim YOK.</div>
      </div>
    </div>`;

  if (s.kind === 'loading' || s.kind === 'idle') {
    return header + `<div class="gb-card"><div class="gb-empty-sm">Yükleniyor…</div></div>`;
  }
  if (s.kind === 'error') {
    return header + `<div class="gb-card"><div class="gb-empty-sm">Liste alınamadı: ${escHtml(s.message)}</div></div>`;
  }

  const d = s.data;
  // Triage kuyruğu: status='new' otomatik yakalanan, işlenmeyi bekleyen sinyaller (dashboard #1).
  const pendingIntakes = d.intakes.filter(isPendingIntake);
  const summary = `<div class="gb-card"><div class="gb-kpi-grid">
    ${kpi('Intake', String(d.counts.intakes))}
    ${kpi('⏳ Triage bekleyen', String(pendingIntakes.length))}
    ${kpi('Delivery', String(d.counts.deliveries))}
    ${kpi('Support Ticket', String(d.counts.supportTickets))}
  </div></div>`;

  const backfillNote = (d.counts.intakes + d.counts.deliveries + d.counts.supportTickets) === 0
    ? `<div class="gb-card" style="margin-top:12px"><div class="muted">Ref tabloları boş — Twenty'den doldurmak için: <code>scripts/twenty-backfill-objects.ts --execute</code> (tek yönlü ayna).</div></div>`
    : '';

  // Triage bekleyen (status='new') sinyaller en üstte — "işlenmeyi bekleyen" görünürlüğü (dashboard #1).
  const sortedIntakes = [...d.intakes].sort(
    (a, b) => Number(isPendingIntake(b)) - Number(isPendingIntake(a)),
  );
  const intakeSub = pendingIntakes.length > 0
    ? `telegram/phone/meeting/website/referral · ⏳ ${pendingIntakes.length} sinyal triage bekliyor`
    : 'telegram/phone/meeting/website/referral';
  const intakeCard = objectCard(
    'Intake — gelen ilk sinyal', intakeSub,
    d.counts.intakes, sortedIntakes.map(intakeRow).join(''),
    'Henüz intake kaydı yok.',
  );
  const deliveryCard = objectCard(
    'Delivery — teslimat + kabul', 'task/SOP-gateten ayrı izlenir; tutar/ödeme YOK (Finance SoR=Castacc)',
    d.counts.deliveries, d.deliveries.map(deliveryRow).join(''),
    'Henüz delivery kaydı yok.',
  );
  const supportCard = objectCard(
    'Support Ticket — teslim sonrası destek', 'severity + SLA + sahip + müşteri-firma referansı',
    d.counts.supportTickets, d.supportTickets.map(supportRow).join(''),
    'Henüz support ticket yok.',
  );

  return header + summary + backfillNote + intakeCard + deliveryCard + supportCard;
}
